import {$controller} from "rapid/index";
import {countries} from "../../common/address/countries";
import {find} from "lodash";
import {DeleteDialogController} from "../../common/delete-dialog/delete-dialog";
import DeleteDialogTemplate from "../../common/delete-dialog/delete-dialog.html";

const dependencies = ["$Deployment", "$companies", "$Booth", "$Company"];

class DeploymentSettingController extends $controller {

    constructor($injector, $stateParams, $scope, $ToastService) {
        "ngInject";
        super($injector, dependencies);
        this.$scope = $scope;
        this.$stateParams = $stateParams;
        this.toastService = $ToastService;
        this.isNew = true;

        this.status = {
            "draft": "Draft",
            "booked": "Booked",
            "delivery": "Delivery",
            "active": "Active",
            "done": "Done"
        };

        let deployment;
        deployment = this.$Deployment.get({id: $stateParams.cid});

        this.$scope.isLoading = true;
        deployment && deployment.$promise
            .then((deployment) => {
                this.$scope.deployment = deployment;
                this.getCompany(deployment.company);
                this.getBooth(deployment.booth);
            }).catch(() => {
                this.$state.go("deployments");
            });
    }
    getCompany(url) {
        let company = this.$Company.get({url});
        company && company.$promise
            .then((c) => {
                this.$scope.company = c;
                this.$scope.isLoading = false;
            }).catch((e) => {
                console.log(e);
            });
    }
    getBooth(url) {
        let booth = this.$Booth.get({url});
        booth && booth.$promise
            .then((b)=>{
                this.$scope.booth = b;
            }).catch((e) =>{
               console.log(e);
            });

    }
    openDeleteDialog(item) {
        this.$mdDialog.show({
            title: "Deployment Delete",
            controller: DeleteDialogController,
            template: DeleteDialogTemplate,
            targetEvent: event,
            locals: {
                callback: () => {
                    this.deleteItem(item);
                },
                target: "deployment",
                item: item
            }
        });
    }
    deleteItem(item) {
        item.$delete().then(() => {
            this.undoDelete.undoDelete("deployments", this.$filter("getObjectId")(item.url)).then(() => {
                this.toastService.showSimple(`Deletion undone`, 5000);
            }).catch(() => {
                this.toastService.showError(`Could not undone deletion`, 5000);
            });
            this.$state.go("deployments");
        }).catch(() => {
            this.$state.go("deployments");
            this.toastService.showError(`Deployment deleted successfully "${item.name}".`, 5000);
        });
    }


    updateDate(date) {
        date = moment(date).format("DD.MM.YYYY - HH:MM");
        return date;
    }

    updateStatus(status) {
        return status.charAt(0).toUpperCase() + status.slice(1);
    }

    updateCountry(country) {
        let newValue = find(countries, {code: country});
        if (newValue) {
            return newValue.name;
        }
        return null;
    }

    /**
     * Get screen orientation display value
     * @returns {string}
     */
    getScreenOrientation() {
        if (!this.$scope.deployment) {
            return "preselected";
        }
        const mc = this.$scope.deployment.multi_config || this.$scope.deployment.multiConfig;
        if (mc && mc.screen_orientation) {
            return mc.screen_orientation;
        }
        if (mc && mc.screenOrientation) {
            return mc.screenOrientation;
        }
        if (this.$scope.deployment.screenOrientation) {
            return this.$scope.deployment.screenOrientation;
        }
        return "preselected";
    }

    /**
     * Get screen width
     * @returns {number}
     */
    getScreenWidth() {
        if (!this.$scope.deployment) {
            return 1920;
        }
        const mc = this.$scope.deployment.multi_config || this.$scope.deployment.multiConfig;
        if (mc) {
            if (mc.screen_width) return mc.screen_width;
            if (mc.screenWidth) return mc.screenWidth;
        }
        if (this.$scope.deployment.screen && this.$scope.deployment.screen.width) {
            return this.$scope.deployment.screen.width;
        }
        return 1920;
    }

    /**
     * Get screen height
     * @returns {number}
     */
    getScreenHeight() {
        if (!this.$scope.deployment) {
            return 1080;
        }
        const mc = this.$scope.deployment.multi_config || this.$scope.deployment.multiConfig;
        if (mc) {
            if (mc.screen_height) return mc.screen_height;
            if (mc.screenHeight) return mc.screenHeight;
        }
        if (this.$scope.deployment.screen && this.$scope.deployment.screen.height) {
            return this.$scope.deployment.screen.height;
        }
        return 1080;
    }

    /**
     * Get selected resolution label
     * @returns {string}
     */
    getSelectedResolutionLabel() {
        if (!this.$scope.deployment) {
            return "Not selected";
        }
        const mc = this.$scope.deployment.multi_config || this.$scope.deployment.multiConfig;
        if (mc && mc.selected_resolution && mc.selected_resolution.label) {
            return mc.selected_resolution.label;
        }
        if (mc && mc.selectedResolution && mc.selectedResolution.label) {
            return mc.selectedResolution.label;
        }
        return "Not selected";
    }

    /**
     * Get formatted resolution display (e.g., "1080p (1920 x 1080 - Landscape)")
     * @returns {string}
     */
    getFormattedResolution() {
        const width = this.getScreenWidth();
        const height = this.getScreenHeight();

        if (!width || !height) {
            return "Not selected";
        }

        // Determine orientation
        const orientation = width > height ? "Landscape" : "Portrait";

        // Get resolution label if available
        const label = this.getSelectedResolutionLabel();
        if (label && label !== "Not selected") {
            // Try to extract resolution name from label
            // Label format might be like "1080p", "1920x1080", "1080p (1920 x 1080)", etc.
            let resolutionName = "";
            if (label.includes("(")) {
                // Format: "1080p (1920 x 1080)" - extract the part before parenthesis
                resolutionName = label.split("(")[0].trim();
            } else if (label.includes("x") || label.includes("X")) {
                // Format: "1920x1080" - try to extract resolution name
                const parts = label.split(/[xX]/);
                if (parts.length === 2) {
                    const h = parseInt(parts[1].trim());
                    // Common resolution names
                    if (h === 1080) resolutionName = "1080p";
                    else if (h === 720) resolutionName = "720p";
                    else if (h === 1440) resolutionName = "1440p";
                    else if (h === 2160) resolutionName = "4K";
                    else resolutionName = label;
                } else {
                    resolutionName = label;
                }
            } else {
                // Format: "1080p" or similar
                resolutionName = label;
            }

            if (resolutionName) {
                return `${resolutionName} (${width} x ${height} - ${orientation})`;
            }
        }

        // Fallback: just show dimensions and orientation
        return `${width} x ${height} - ${orientation}`;
    }

    /**
     * Get screen orientation text
     * @returns {string}
     */
    getScreenOrientationText() {
        const orientation = this.getScreenOrientation();
        return orientation === "preselected" ? "Choose a Device / Resolution" : "Custom Resolution";
    }

    /**
     * Get multi-config active status
     * @returns {boolean}
     */
    isMultiConfigActive() {
        if (!this.$scope.deployment) {
            return false;
        }

        const mc = this.$scope.deployment.multi_config || this.$scope.deployment.multiConfig;
        const hasAreas = mc && mc.configurations_info && mc.configurations_info.length > 0;
        const hasTouchPoints = mc && mc.configList && mc.configList.length > 0;
        const hasConfigsLegacy = this.$scope.deployment.configurations_info && this.$scope.deployment.configurations_info.length > 0;

        if (mc && mc.active !== undefined) {
            return mc.active === true || hasAreas || hasTouchPoints || hasConfigsLegacy;
        }

        return hasAreas || hasTouchPoints || hasConfigsLegacy;
    }

    isShowMultiConfigFirst() {
        if (!this.$scope.deployment) {
            return false;
        }
        const mc = this.$scope.deployment.multi_config || this.$scope.deployment.multiConfig;
        if (mc && mc.show_multi_config_before_first_session !== undefined) {
            return !!mc.show_multi_config_before_first_session;
        }
        if (mc && mc.showMultiConfigBeforeFirstSession !== undefined) {
            return !!mc.showMultiConfigBeforeFirstSession;
        }
        const d = this.$scope.deployment;
        if (d.show_multi_config_before_first_session !== undefined) {
            return !!d.show_multi_config_before_first_session;
        }
        if (d.showMultiConfigBeforeFirstSession !== undefined) {
            return !!d.showMultiConfigBeforeFirstSession;
        }
        return false;
    }

    /**
     * Get configurations info for display
     * @returns {Array}
     */
    getConfigurationsInfo() {
        if (!this.$scope.deployment) {
            return [];
        }
        const mc = this.$scope.deployment.multi_config || this.$scope.deployment.multiConfig;
        if (mc && mc.configurations_info) {
            return mc.configurations_info;
        }
        return this.$scope.deployment.configurations_info || [];
    }

    /**
     * Get multi-config background
     * @returns {Object|null}
     */
    getMultiConfigBackground() {
        if (!this.$scope.deployment) {
            return null;
        }
        const mc = this.$scope.deployment.multi_config || this.$scope.deployment.multiConfig;
        return mc && mc.background ? mc.background : null;
    }

    /**
     * Get background file url (image/video) for preview
     * @returns {string}
     */
    getMultiConfigBackgroundFile() {
        const bg = this.getMultiConfigBackground();
        if (!bg) return "";
        // prefer attachment_file, fallback to thumbnail_file
        if (bg.attachment_file) return bg.attachment_file;
        if (bg.thumbnail_file) return bg.thumbnail_file;
        return "";
    }

    // Standby helpers (read-only)
    getStandbyAvailable() {
        if (!this.$scope.deployment) return false;
        const mc = this.$scope.deployment.multi_config || this.$scope.deployment.multiConfig;
        return !!mc;
    }

    isStandbyEnabled() {
        const mc = this.$scope.deployment && (this.$scope.deployment.multi_config || this.$scope.deployment.multiConfig);
        return !!(mc && mc.standby_screen);
    }

    getStandbyActivateTime() {
        const mc = this.$scope.deployment && (this.$scope.deployment.multi_config || this.$scope.deployment.multiConfig);
        return mc && mc.standby_screen_activate_time !== undefined ? mc.standby_screen_activate_time : 0;
    }

    getStandbyAfterEachSession() {
        const mc = this.$scope.deployment && (this.$scope.deployment.multi_config || this.$scope.deployment.multiConfig);
        return !!(mc && mc.standby_screen_after_each_session);
    }

    getStandbyImage() {
        const mc = this.$scope.deployment && (this.$scope.deployment.multi_config || this.$scope.deployment.multiConfig);
        const img = mc && mc.standby_screen_image;
        if (!img) return "";
        return img.attachment_file || img.thumbnail_file || "";
    }

    getStandbyVideoBackground() {
        const file = this.getStandbyImage();
        if (!file) return "";
        const ext = (file.split(".").pop() || "").toLowerCase();
        return ext === "mp4" ? file : "";
    }

    /**
     * Get multi-config areas for preview
     * @returns {Object}
     */
    getMultiConfigAreas() {
        if (!this.$scope.deployment) {
            return {};
        }

        const mc = this.$scope.deployment.multi_config || this.$scope.deployment.multiConfig || {};
        const configs = mc.configurations_info || this.$scope.deployment.configurations_info || [];
        const areas = {};

        configs.forEach((cfg, index) => {
            const key = `config${cfg.config_index !== undefined ? cfg.config_index : index}`;
            const icon = cfg.custom_icon || null;
            areas[key] = {
                width: cfg.width || 25,
                height: cfg.height || 25,
                x: cfg.x || 20,
                y: cfg.y || 60,
                angle: cfg.angle || 0,
                customIcon: icon ? {
                    attachment_file: icon.attachment_file || "",
                    thumbnail_file: icon.thumbnail_file || "",
                    first_frame: icon.first_frame || "",
                    original_name: icon.original_name || ""
                } : null
            };
        });

        return areas;
    }

    /**
     * Get video background for multi config preview
     * @returns {string}
     */
    getMultiConfigVideoBackground() {
        const file = this.getMultiConfigBackgroundFile();
        if (!file) return "";
        const parts = file.split(".");
        const extension = parts.length > 1 ? parts.pop().toLowerCase() : "";
        return extension === "mp4" ? file : "";
    }

    /**
     * Get config touch point by index
     * @param {number} index
     * @returns {Object|null}
     */
    getConfigTouchPoint(index) {
        if (!this.$scope.deployment) {
            return null;
        }
        const mc = this.$scope.deployment.multi_config || this.$scope.deployment.multiConfig;
        if (!mc || !mc.configurations_info) {
            return null;
        }
        return mc.configurations_info.find(cfg => cfg.config_index === index) || null;
    }

    /**
     * Get background mode for html-preview
     * @returns {string}
     */
    getBackgroundMode() {
        const background = this.getMultiConfigBackground();
        return background && background.start_screen_background_mode ? background.start_screen_background_mode : "image";
    }

    /**
     * Get background color for html-preview
     * @returns {string}
     */
    getBackgroundColor() {
        const background = this.getMultiConfigBackground();
        return background && background.start_screen_background_color ? background.start_screen_background_color : "#ffffff";
    }

    /**
     * Get background style for html-preview
     * @returns {string}
     */
    getBackgroundStyle() {
        const background = this.getMultiConfigBackground();
        return background && background.start_screen_image_style ? background.start_screen_image_style : "stretch";
    }

    /**
     * Get background transparency for html-preview
     * @returns {boolean}
     */
    getBackgroundTransparency() {
        const background = this.getMultiConfigBackground();
        return background && background.start_screen_live_view ? background.start_screen_live_view : false;
    }
}

export default DeploymentSettingController;



// WEBPACK FOOTER //
// ./sources/components/deployments/deployment-setting/deployment-setting.controller.js